<template>
    <div class="received-email-listing">
        <TableLoading v-if="initLoading" />
        <template v-else>
            <EmailListingSettingsHeader
                ref="ReceivedEmailsTable"
                :columns="columns"
                :totalEmails="pagination.total"
                :displayTotalResults="false"
                :searchValue="searchValue"
                :customTotalResultsLabel="customTotalResultsLabel"
                searchable
                refreshable
                class="received-email-listing__settings-header"
                @search="handleSearch"
                @refresh="searchProviderThreads"
            />
            <div class="received-email-listing__select-all">
                <CompassCheckbox
                    :modelValue="true"
                    :label="$t.__('common.actions.select_all')"
                    :checked="selectedEmails.length === emails.length"
                    :indeterminate="isCheckboxIndeterminate"
                    @change="handleCheckAll"
                />
            </div>
            <TableLoading v-if="isLoading" />
            <EmailListingTable
                v-else
                :columns="columns"
                :emails="emails"
                :isLoading="isLoading"
                :hasActiveFilters="searchValue.length > 0"
                :selectedEmails="selectedEmails"
                class="received-email-listing__table"
                @update:readingStatus="updateReadingStatus"
                @resetFilters="handleSearch('')"
                @open:related="handleOpenLinkObjectDialog"
                @update:checkedEmails="handleCheckEmails"
            />
            <EmailListingPagination
                v-if="pagination.total > 0"
                :isLoading="isLoading"
                :pagination="pagination"
                :displayTotalResults="false"
                @update-pagination="updatePagination"
            />
        </template>

        <EmailsListingMassActions
            :selectedEmails="selectedEmails"
            :isToolbarVisible="selectedEmails.length > 0"
            @closeToolbar="handleCloseMassActions"
            @open:related="handleOpenLinkObjectDialogFromMassActions"
        />
        <LinkObjectDialog
            :isVisible="showLinkObjectDialog"
            :related="relatedObjects"
            :isLoading="linkObjectDialogLoading"
            :hasError="hasRelatedUpdateError"
            :emptyStateData="emptyStateData"
            @close="handleCloseLinkObjectDialog"
            @update:related="updateRelatedObjects"
        />
    </div>
</template>

<script>
import EmailListingPagination from './EmailListingPagination.vue';
import EmailListingSettingsHeader from 'vcomponents/email/listings/EmailListingSettingsHeader.vue';
import EmailListingTable from 'vcomponents/email/listings/EmailListingTable.vue';
import EmailsListingMassActions from 'vcomponents/email/listings/EmailsListingMassActions.vue';
import EmailsListingMixins from 'mixins/emails/EmailsListingMixins';
import LinkedMixins from 'mixins/linked/LinkedMixins';
import LinkObjectDialog from 'vcomponents/common/object/LinkObjectDialog.vue';
import {
    getEmailSettings,
    searchProviderThreads,
    updateProviderThread,
    updateProviderThreadMessagesRelatedBatch,
} from 'api/emailEngine/emailEngineService';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';

export default {
    components: {
        EmailListingTable,
        EmailListingSettingsHeader,
        EmailListingPagination,
        LinkObjectDialog,
        EmailsListingMassActions,
        TableLoading,
    },
    mixins: [EmailsListingMixins, LinkedMixins],
    data() {
        return {
            emails: [],
            columns: [
                {
                    code: 'participants',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    order: 0,
                    sortKey: 'from',
                },
                {
                    code: 'subject',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    order: 1,
                    sortKey: 'subject',
                },
                {
                    code: 'related',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    order: 2,
                    sortKey: 'related',
                },
                {
                    code: 'date',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    order: 3,
                    sortKey: 'date',
                },
            ],
            currentQueryParams: {
                limit: 25,
                offset: null,
            },
            initLoading: true,
            isLoading: false,
            searchValue: '',
            pagination: {
                limit: 25,
                total: 0,
            },
            showLinkObjectDialog: false,
            emailsToUpdate: [],
            hasRelatedUpdateError: false,
            linkObjectDialogLoading: false,
            debounceTimer: null,
            selectedEmails: [],
            currentUserEmail: null,
        };
    },
    computed: {
        customTotalResultsLabel() {
            const totalResults = this.pagination.total;
            if (totalResults === 0) {
                return this.$t.__('email.listings.ReceivedEmailListing.no_results');
            }

            return this.$t.n__(
                'email.listings.ReceivedEmailListing.total_results',
                null,
                totalResults,
                totalResults,
            );
        },
        relatedObjects() {
            return this.emailsToUpdate.length === 1 ? this.emailsToUpdate[0].related : [];
        },
        isCheckboxIndeterminate() {
            return (
                this.selectedEmails.length > 0 && this.selectedEmails.length < this.emails.length
            );
        },
    },
    created() {
        getEmailSettings()
            .then((response) => {
                this.currentUserEmail = response.data.connection.email;
            })
            .finally(() => {
                this.searchProviderThreads();
            });
    },
    methods: {
        searchProviderThreads() {
            this.isLoading = true;
            const filters = {
                folder: 'inbox',
                search: this.searchValue,
            };
            searchProviderThreads(filters, this.currentQueryParams)
                .then((response) => {
                    this.emails = response.data.map((email) => ({
                        ...email,
                        participants: this.sortParticipants(email.participants),
                        related: this.formatRelatedObjects(email.related),
                        canRead: this.canReadEmail(email._links),
                        canUpdate: this.canUpdateEmail(email._links),
                        canUpdateRelated: this.canUpdateRelated(email._links),
                        isChecked: false,
                    }));
                    this.pagination = response.pagination;
                    this.currentQueryParams = {
                        limit: response.pagination.limit,
                        offset: null,
                    };
                })
                .catch((error) => {
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: error,
                    });
                })
                .finally(() => {
                    this.initLoading = false;
                    this.isLoading = false;
                });
        },
        handleSearch(newSearchValue) {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            const wait = 500;
            this.debounceTimer = setTimeout(() => {
                this.searchValue = newSearchValue;
                this.searchProviderThreads();
            }, wait);
        },
        updateReadingStatus({ emailProviderThreadId, unreadStatus = null, showToaster = true }) {
            const threadToUpdate = this.emails.find(
                (email) => email.provider_thread_id === emailProviderThreadId,
            );
            const updateProviderThreadLink = threadToUpdate._links.find(
                (link) => link.rel === 'thread.update',
            )?.href;

            updateProviderThread(updateProviderThreadLink, {
                unread: unreadStatus ?? !threadToUpdate.unread,
            })
                .then((response) => {
                    if (showToaster) {
                        this.$pushToast({
                            type: 'success',
                            close: true,
                            text: this.$t.__(
                                'email.listings.ReceivedEmailListing.success_updating_reading_status',
                                {
                                    status: response.unread
                                        ? this.$t.__('email.listings.reading_status.unread')
                                        : this.$t.__('email.listings.reading_status.read'),
                                },
                            ),
                        });
                    }

                    this.emails = this.emails.map((email) => {
                        if (email.provider_thread_id === response.provider_thread_id) {
                            email.unread = response.unread;
                        }
                        return email;
                    });
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__(
                            'email.listings.ReceivedEmailListing.error_updating_reading_status',
                        ),
                    });
                });
        },
        updatePagination(pagination) {
            this.currentQueryParams = {
                limit: pagination.limit,
                offset: pagination.offset,
            };
            this.searchProviderThreads();
        },
        updateRelatedObjects(newRelatedObjects) {
            this.linkObjectDialogLoading = true;
            this.hasRelatedUpdateError = false;

            const updateProviderThreadMessagesRelatedBatchLink = this.emailsToUpdate[0]._links.find(
                (link) => link.rel === 'thread.update_related_batch',
            )?.href;

            const formatedData = this.emailsToUpdate.map((emailToUpdate) => ({
                type: 'thread',
                id: emailToUpdate.provider_thread_id,
                related:
                    this.emailsToUpdate.length === 1
                        ? this.formatAndSimplifyRelatedObjects(newRelatedObjects)
                        : this.formatAndSimplifyRelatedObjects(
                            this.mergeRelatedObjects(emailToUpdate.related, newRelatedObjects),
                        ),
            }));
            updateProviderThreadMessagesRelatedBatch(
                updateProviderThreadMessagesRelatedBatchLink,
                formatedData,
            )
                .then(() => {
                    this.emails = this.emails.map((email) => {
                        const emailToUpdate = this.emailsToUpdate.find(
                            (emailToUpdate) =>
                                emailToUpdate.provider_thread_id === email.provider_thread_id,
                        );
                        if (emailToUpdate) {
                            email.related =
                                this.emailsToUpdate.length === 1
                                    ? newRelatedObjects
                                    : this.mergeRelatedObjects(email.related, newRelatedObjects);
                        }
                        return email;
                    });

                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__(
                            'email.listings.ReceivedEmailListing.success_updating_related_objects',
                        ),
                    });
                    this.hasRelatedUpdateError = false;
                    this.handleCloseLinkObjectDialog();
                })
                .catch(() => {
                    this.hasRelatedUpdateError = true;
                })
                .finally(() => {
                    this.linkObjectDialogLoading = false;
                });
        },
        handleOpenLinkObjectDialog(emailToUpdate) {
            this.emailsToUpdate.push(emailToUpdate);
            this.showLinkObjectDialog = true;
        },
        handleCloseLinkObjectDialog() {
            this.emailsToUpdate = [];
            this.selectedEmails = [];
            this.showLinkObjectDialog = false;
        },
        handleCheckEmails(emails) {
            this.selectedEmails = emails;
        },
        handleCheckAll() {
            this.handleCheckEmails(
                this.selectedEmails.length === this.emails.length ? [] : this.emails,
            );
        },
        handleCloseMassActions() {
            this.emailsToUpdate = [];
            this.selectedEmails = [];
        },
        handleOpenLinkObjectDialogFromMassActions() {
            this.emailsToUpdate = this.selectedEmails;
            this.showLinkObjectDialog = true;
        },
        sortParticipants(participants) {
            const currentParticipant = participants.find(
                (participant) => participant.email === this.currentUserEmail,
            );
            const sortedParticipants = participants.filter(
                (participant) => participant.email !== this.currentUserEmail,
            );

            if (currentParticipant) {
                sortedParticipants.push(currentParticipant);
            }

            return sortedParticipants;
        },
    },
};
</script>
<style lang="scss" scoped>
.received-email-listing {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-radius: var(--spacing-0);
    padding: 0 var(--spacing-3);
    background: var(--colors-common-background-default);

    &__settings-header {
        align-items: center; // to be removed when filters are added to the header
        min-height: 64px;
        flex-shrink: 0;
    }

    &__select-all {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-shrink: 0;
        height: 48px;
        padding: 0 var(--spacing-3);
        background-color: var(--colors-common-background-default);
        border-width: 0px;
        border-bottom-width: 1px;
        border-style: solid;
        border-bottom-color: var(--colors-common-border-lighter);
    }

    &__table {
        flex-shrink: 1;
        overflow: auto;
    }

    &:has(.email-listing__pagination) {
        &::v-deep {
            .compass-table,
            .email-listing-loader {
                border-bottom: none;
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }
        }
    }
}
</style>
